import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useGetCatalogItemsApi } from './use-get-catalog-items-api';

const endpoint = 'catalog/v1/restaurants';
export const updateCatalogItemEndpointIdentifier = endpoint;

interface BodyType {
  name: {
    en: string;
    ar: string | null;
  };
  brand: {
    en: string | null;
    ar: string | null;
  };
  sku: string | null;
  kasoId: string;
  unitId: number;
  price: number;
  description: string | null;
  images: string[];
}

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  itemId: string;
  body: BodyType;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useUpdateCatalogItemApi(restaurantId: string, warehouseId: string, options?: Options): ReturnType {
  const { refetch: refetchCatalogItems } = useGetCatalogItemsApi(restaurantId, warehouseId, { enabled: false });
  const toast = useCustomToast();
  let fullEndpoint;

  const mutationFn = async ({ body, itemId }: TVariables) => {
    fullEndpoint = `${endpoint}/${restaurantId}/warehouses/${warehouseId}/items/${itemId}`;

    try {
      const { data } = await http.put(fullEndpoint, body);

      // Refetch warehouse items
      refetchCatalogItems();

      toast({
        description: 'New item have been updated successfully',
        status: 'success',
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { mutationKey: fullEndpoint, ...options });
}
